import {
  Checkbox,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Switch,
  useMediaQuery,
} from "@mui/material";
import "./GeneralSettings.scss";
import { useState, useEffect } from "react";
import TimerIcon from "@mui/icons-material/Timer";
import PercentIcon from "@mui/icons-material/Percent";
import BlueButton from "../../../components/BlueButton/BlueButton";
import api from "../../../utils/api";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";
import { usePermissions } from "../../../hooks/usePermissions";

const GeneralSettings = () => {
  const { hasPermission } = usePermissions();
  const isMobile = useMediaQuery("(max-width:767px)");

  // VAT state
  const [vatEnabled, setVatEnabled] = useState(false);
  const [vatPercentage, setVatPercentage] = useState("");

  // Working hours state
  const [workingHours, setWorkingHours] = useState({
    from: "09:00",
    to: "18:00",
  });

  // Booking availability state
  const [bookingAvailability, setBookingAvailability] = useState(true);

  // Normal booking availability state
  const [normalBookingAvailability, setNormalBookingAvailability] =
    useState(true);

  // Minimum price for single booking state
  const [minimumPrice, setMinimumPrice] = useState("");

  // Visit period state
  const [visitPeriod, setVisitPeriod] = useState("");

  // Form submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check permissions
  const canView = hasPermission(94); // رؤية الاعدادات العامة
  const canEdit = hasPermission(95); // تعديل الاعدادات العامة

  // Fetch data from API on component mount
  useEffect(() => {
    const fetchSettings = async () => {
      // Check view permission
      if (!canView) {
        toast.error("ليس لديك صلاحية لعرض الإعدادات العامة");
        setIsLoading(false);
        return;
      }

      try {
        const response = await api.get("/system");
        const { data } = response.data;

        // Set state with fetched data
        setVatEnabled(data.VAT.isActive);
        setVatPercentage(data.VAT.value.toString());

        // Convert 24H time format to 12H for display
        setWorkingHours({
          from: convertTo12Hour(data.workingHours.from),
          to: convertTo12Hour(data.workingHours.to),
        });

        setVisitPeriod(data.visitPeriod.toString());
        setBookingAvailability(data.bookingAvailability);
        setNormalBookingAvailability(data.normalBookingAvailability);

        // Set minimum price if it exists in the response
        setMinimumPrice(data.minimumPrice ? data.minimumPrice.toString() : "");

        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching settings:", error);
        toast.error("فشل في جلب الإعدادات العامة");
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, [canView]);

  // Convert time from 24-hour format to 12-hour format
  const convertTo12Hour = (time24) => {
    return time24;
  };

  // Convert time from 12-hour format to 24-hour format
  const convertTo24Hour = (time12) => {
    return time12;
  };

  // Handle working hours change
  const handleWorkingHoursChange = (field, value) => {
    if (!canEdit) {
      toast.error("ليس لديك صلاحية لتعديل الإعدادات العامة");
      return;
    }
    setWorkingHours({
      ...workingHours,
      [field]: value,
    });
  };

  // Handle minimum price change with validation
  const handleMinimumPriceChange = (e) => {
    if (!canEdit) {
      toast.error("ليس لديك صلاحية لتعديل الإعدادات العامة");
      return;
    }

    const value = e.target.value;

    // Allow empty value for clearing the input
    if (value === "") {
      setMinimumPrice("");
      return;
    }

    // Only allow positive numbers with up to 2 decimal places
    const regex = /^\d*\.?\d{0,2}$/;
    if (regex.test(value) && parseFloat(value) >= 0) {
      setMinimumPrice(value);
    }
  };

  // Handle VAT changes
  const handleVatEnabledChange = (e) => {
    if (!canEdit) {
      toast.error("ليس لديك صلاحية لتعديل الإعدادات العامة");
      return;
    }
    setVatEnabled(e.target.checked);
  };

  const handleVatPercentageChange = (e) => {
    if (!canEdit) {
      toast.error("ليس لديك صلاحية لتعديل الإعدادات العامة");
      return;
    }
    setVatPercentage(e.target.value);
  };

  // Handle booking availability changes
  const handleBookingAvailabilityChange = (e) => {
    if (!canEdit) {
      toast.error("ليس لديك صلاحية لتعديل الإعدادات العامة");
      return;
    }
    setBookingAvailability(e.target.checked);
  };

  const handleNormalBookingAvailabilityChange = (e) => {
    if (!canEdit) {
      toast.error("ليس لديك صلاحية لتعديل الإعدادات العامة");
      return;
    }
    setNormalBookingAvailability(e.target.checked);
  };

  // Handle visit period change
  const handleVisitPeriodChange = (e) => {
    if (!canEdit) {
      toast.error("ليس لديك صلاحية لتعديل الإعدادات العامة");
      return;
    }
    setVisitPeriod(e.target.value);
  };

  const handleSaveGeneralSettings = async () => {
    // Check edit permission
    if (!canEdit) {
      toast.error("ليس لديك صلاحية لتعديل الإعدادات العامة");
      return;
    }

    // Set submitting state
    setIsSubmitting(true);

    // Validate minimum price if normal booking is enabled
    if (
      normalBookingAvailability &&
      (!minimumPrice || parseFloat(minimumPrice) < 0)
    ) {
      toast.error("يرجى إدخال سعر صحيح للزيارات الفردية");
      setIsSubmitting(false);
      return;
    }

    // Prepare data for API call
    const data = {
      VAT: {
        value: Number(vatPercentage),
        isActive: vatEnabled,
      },
      workingHours: {
        from: convertTo24Hour(workingHours.from),
        to: convertTo24Hour(workingHours.to),
      },
      bookingAvailability,
      normalBookingAvailability,
      visitPeriod: Number(visitPeriod),
      // Add minimum price to the data being sent
      minimumPrice: normalBookingAvailability ? Number(minimumPrice) : 0,
    };

    try {
      // Make API call to save settings
      const response = await api.patch("/system/generalSettings", data);

      if (response.data.success) {
        toast.success("تم حفظ البيانات بنجاح");
      } else {
        toast.error("حدث خطأ أثناء حفظ البيانات");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("حدث خطأ أثناء حفظ البيانات");
    } finally {
      setIsSubmitting(false);
    }
  };

  // If no view permission, show restricted access
  if (!canView) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "300px",
          backgroundColor: "white",
          borderRadius: "8px",
        }}
      >
        <p
          style={{
            fontSize: "18px",
            color: "#666",
            textAlign: "center",
          }}
        >
          ليس لديك صلاحية لعرض الإعدادات العامة
        </p>
      </div>
    );
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="section-container">
      <div className="options-container">
        <div className="view-option">
          {/* 1. Working Hours Section */}
          <div className="working-hours-container">
            <p className="subsection-title">مواعيد بدء العمل اليومية</p>
            <div className="hours-inputs">
              <FormControl sx={{ width: "240px" }} variant="outlined">
                <InputLabel
                  htmlFor="working-hours-from"
                  shrink
                  sx={{
                    background: "#f9fafb",
                    transformOrigin: "right top",
                    transform: "translate(14px, -9px) scale(0.8)",
                    right: 24,
                    left: "auto",
                    paddingLeft: "4px",
                    paddingRight: "4px",
                  }}
                >
                  من
                </InputLabel>
                <OutlinedInput
                  sx={{
                    borderRadius: "10px",
                    "& input": {
                      textAlign: "center",
                    },
                  }}
                  required
                  id="working-hours-from"
                  type="time"
                  value={workingHours.from}
                  onChange={(e) =>
                    handleWorkingHoursChange("from", e.target.value)
                  }
                  disabled={!canEdit}
                />
              </FormControl>

              <FormControl sx={{ width: "240px" }} variant="outlined">
                <InputLabel
                  htmlFor="working-hours-to"
                  shrink
                  sx={{
                    background: "#f9fafb",
                    transformOrigin: "right top",
                    transform: "translate(14px, -9px) scale(0.8)",
                    right: 24,
                    left: "auto",
                    paddingLeft: "4px",
                    paddingRight: "4px",
                  }}
                >
                  إلى
                </InputLabel>
                <OutlinedInput
                  sx={{
                    borderRadius: "10px",
                    "& input": {
                      textAlign: "center",
                    },
                  }}
                  required
                  id="working-hours-to"
                  type="time"
                  value={workingHours.to}
                  onChange={(e) =>
                    handleWorkingHoursChange("to", e.target.value)
                  }
                  disabled={!canEdit}
                />
              </FormControl>
            </div>
            {!canEdit && (
              <p
                style={{
                  fontSize: "12px",
                  color: "#666",
                  fontStyle: "italic",
                  marginTop: "8px",
                }}
              >
                ليس لديك صلاحية لتعديل مواعيد العمل
              </p>
            )}
          </div>

          {/* 2. Visit Period Section */}
          <div className="visit-period-container">
            <div className="subsection-header">
              <p className="subsection-title">مدة الزيارة</p>
              <div className="tooltip-text">
                مدة الزيارة المعتادة بالساعات. تستخدم لحساب الجدول الزمني
              </div>
            </div>
            <FormControl sx={{ width: "240px" }} variant="outlined">
              <InputLabel
                htmlFor="visit-period"
                shrink
                sx={{
                  background: "#f9fafb",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.8)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                مدة الزيارة (بالساعة)
              </InputLabel>
              <OutlinedInput
                sx={{
                  borderRadius: "10px",
                  "& input": {
                    textAlign: "center",
                  },
                }}
                required
                id="visit-period"
                type="text"
                inputProps={{ min: "0.5", step: "0.5" }}
                value={visitPeriod}
                onChange={handleVisitPeriodChange}
                disabled={!canEdit}
                endAdornment={
                  <InputAdornment position="end">
                    <TimerIcon sx={{ color: "#039ced", fontSize: 20 }} />
                  </InputAdornment>
                }
              />
            </FormControl>
            {!canEdit && (
              <p
                style={{
                  fontSize: "12px",
                  color: "#666",
                  fontStyle: "italic",
                  marginTop: "8px",
                }}
              >
                ليس لديك صلاحية لتعديل مدة الزيارة
              </p>
            )}
          </div>

          {/* 3. General Booking Availability Section */}
          <div
            className={`booking-availability-container ${
              bookingAvailability ? "enabled" : ""
            }`}
          >
            <div className="subsection-header">
              <p className="subsection-title">خيارات الحجز العامة</p>
              <div className="tooltip-text">
                عند تعطيل هذا الخيار، لن يتمكن العملاء من حجز مواعيد حتى يعاد
                تشغيله مرة أخرى
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={bookingAvailability}
                    onChange={handleBookingAvailabilityChange}
                    disabled={!canEdit}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#039ced",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "#039ced",
                        },
                    }}
                  />
                }
                label="إتاحة الحجز العام"
                sx={{
                  marginRight: 0,
                  padding: "8px 12px",
                  backgroundColor: bookingAvailability
                    ? "rgba(3, 156, 237, 0.08)"
                    : "transparent",
                  borderRadius: "8px",
                  transition: "all 0.2s ease",
                  width: "240px",
                }}
              />
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  color: bookingAvailability ? "#28a745" : "#dc3545",
                  backgroundColor: bookingAvailability
                    ? "rgba(40, 167, 69, 0.1)"
                    : "rgba(220, 53, 69, 0.1)",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  minWidth: "60px",
                  textAlign: "center",
                }}
              >
                {bookingAvailability ? "متاح" : "غير متاح"}
              </span>
            </div>
            {!canEdit && (
              <p
                style={{
                  fontSize: "12px",
                  color: "#666",
                  fontStyle: "italic",
                  marginTop: "8px",
                }}
              >
                ليس لديك صلاحية لتعديل خيارات الحجز العامة
              </p>
            )}
          </div>

          {/* 4. Individual Booking Availability with Minimum Price Section */}
          <div
            className={`booking-availability-container ${
              normalBookingAvailability ? "enabled" : ""
            }`}
          >
            <div className="subsection-header">
              <p className="subsection-title">إتاحة الزيارات الفردية</p>
              <div className="tooltip-text">
                عند تعطيل هذا الخيار، لن يتمكن العملاء من حجز زيارات فردية
              </div>
            </div>

            <div
              className="vat-input-control"
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "40px",
                flexWrap: "wrap",
              }}
            >
              {/* Switch and Status */}
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={normalBookingAvailability}
                      onChange={handleNormalBookingAvailabilityChange}
                      disabled={!canEdit}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#039ced",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#039ced",
                          },
                      }}
                    />
                  }
                  label="إتاحة الزيارات الفردية"
                  sx={{
                    marginRight: 0,
                    padding: "8px 12px",
                    backgroundColor: normalBookingAvailability
                      ? "rgba(3, 156, 237, 0.08)"
                      : "transparent",
                    borderRadius: "8px",
                    transition: "all 0.2s ease",
                    width: "240px",
                  }}
                />
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: normalBookingAvailability ? "#28a745" : "#dc3545",
                    backgroundColor: normalBookingAvailability
                      ? "rgba(40, 167, 69, 0.1)"
                      : "rgba(220, 53, 69, 0.1)",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    minWidth: "60px",
                    textAlign: "center",
                  }}
                >
                  {normalBookingAvailability ? "متاح" : "غير متاح"}
                </span>
              </div>

              {/* Minimum Price Input */}
              <div>
                <FormControl sx={{ width: "240px" }} variant="outlined">
                  <InputLabel
                    htmlFor="minimum-price"
                    shrink
                    sx={{
                      background: "#f9fafb",
                      transformOrigin: "right top",
                      transform: "translate(14px, -9px) scale(0.8)",
                      right: 24,
                      left: "auto",
                      paddingLeft: "4px",
                      paddingRight: "4px",
                    }}
                  >
                    الحد الأدنى للسعر (ريال)
                  </InputLabel>
                  <OutlinedInput
                    sx={{
                      borderRadius: "10px",
                      "& input": {
                        textAlign: "center",
                      },
                    }}
                    required={normalBookingAvailability}
                    id="minimum-price"
                    type="text"
                    inputProps={{
                      min: "0",
                      step: "0.01",
                      pattern: "[0-9]+(\\.[0-9]{1,2})?",
                    }}
                    placeholder="0.00 ريال"
                    value={minimumPrice}
                    onChange={handleMinimumPriceChange}
                    disabled={!normalBookingAvailability || !canEdit}
                    endAdornment={
                      <InputAdornment position="end">
                        <span
                          style={{
                            color: "#039ced",
                            fontSize: "14px",
                            fontWeight: "bold",
                            marginRight: "4px",
                          }}
                        >
                          ر.س
                        </span>
                      </InputAdornment>
                    }
                    error={
                      normalBookingAvailability &&
                      (!minimumPrice || parseFloat(minimumPrice) < 0)
                    }
                  />
                </FormControl>
                {normalBookingAvailability &&
                  (!minimumPrice || parseFloat(minimumPrice) < 0) && (
                    <div
                      style={{
                        color: "#dc3545",
                        fontSize: "12px",
                        marginTop: "4px",
                        paddingRight: "14px",
                      }}
                    >
                      يرجى إدخال سعر صحيح
                    </div>
                  )}
              </div>
            </div>
            {!canEdit && (
              <p
                style={{
                  fontSize: "12px",
                  color: "#666",
                  fontStyle: "italic",
                  marginTop: "8px",
                }}
              >
                ليس لديك صلاحية لتعديل إعدادات الزيارات الفردية
              </p>
            )}
          </div>

          {/* 5. VAT Settings Section */}
          <div className={`vat-container ${vatEnabled ? "enabled" : ""}`}>
            <div className="subsection-header">
              <p className="subsection-title">إعدادات الضريبة</p>
              <div className="tooltip-text">
                سيتم إضافة ضريبة القيمة المضافة إلى سعر الفاتورة
              </div>
            </div>
            <div
              className="vat-input-control"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "20px",
                flexWrap: "wrap",
              }}
            >
              <FormControl sx={{ width: "240px" }} variant="outlined">
                <InputLabel
                  htmlFor="outlined-vat-percentage"
                  shrink
                  sx={{
                    background: "#f9fafb",
                    transformOrigin: "right top",
                    transform: "translate(14px, -9px) scale(0.8)",
                    right: 24,
                    left: "auto",
                    paddingLeft: "4px",
                    paddingRight: "4px",
                  }}
                >
                  الضريبة (%)
                </InputLabel>
                <OutlinedInput
                  sx={{
                    borderRadius: "10px",
                    "& input": {
                      textAlign: "center",
                    },
                  }}
                  required
                  id="outlined-vat-percentage"
                  type="number"
                  inputProps={{ min: "0", max: "100" }}
                  placeholder="%"
                  notched
                  value={vatPercentage}
                  onChange={handleVatPercentageChange}
                  disabled={!vatEnabled || !canEdit}
                  endAdornment={
                    <InputAdornment position="end">
                      <PercentIcon sx={{ color: "#039ced", fontSize: 20 }} />
                    </InputAdornment>
                  }
                />
              </FormControl>

              <FormControlLabel
                className={vatEnabled ? "selected" : ""}
                control={
                  <Checkbox
                    checked={vatEnabled}
                    onChange={handleVatEnabledChange}
                    disabled={!canEdit}
                    sx={{
                      color: "#c4c4c4",
                      "&.Mui-checked": {
                        color: "#039ced",
                      },
                    }}
                  />
                }
                label="تطبيق ضريبة القيمة المضافة"
                sx={{
                  marginRight: 0,
                  padding: "8px 12px",
                  backgroundColor: vatEnabled
                    ? "rgba(3, 156, 237, 0.08)"
                    : "transparent",
                  borderRadius: "8px",
                  transition: "all 0.2s ease",
                  minWidth: "250px",
                }}
              />
            </div>
            {!canEdit && (
              <p
                style={{
                  fontSize: "12px",
                  color: "#666",
                  fontStyle: "italic",
                  marginTop: "8px",
                }}
              >
                ليس لديك صلاحية لتعديل إعدادات الضريبة
              </p>
            )}
          </div>

          {/* 6. Save Button Section */}
          {canEdit ? (
            <div className="save-button-container">
              <BlueButton
                text={isSubmitting ? "جاري الحفظ..." : "حفظ التغييرات"}
                variant="fill"
                size={isMobile ? "full" : "large"}
                onClick={handleSaveGeneralSettings}
                disabled={isSubmitting}
                className="blue-button"
              />
            </div>
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: "20px",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                border: "1px solid #e9ecef",
                marginTop: "20px",
              }}
            >
              <p
                style={{
                  fontSize: "16px",
                  color: "#666",
                  margin: 0,
                }}
              >
                أنت في وضع العرض فقط - ليس لديك صلاحية لتعديل الإعدادات العامة
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GeneralSettings;
